import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../utils/api';
import { waitMinutes } from '../components/RegisterPanel';

/**
 * Ask for a reset link: /forgot-password
 *
 * THE SUCCESS MESSAGE IS THE SAME WHETHER OR NOT THE ADDRESS HAS AN ACCOUNT,
 * AND THAT IS THE POINT.
 *
 * The server answers identically either way (routes/auth.py::forgot_password)
 * so that the endpoint cannot be used to find out who banks here. If this
 * page then said "no account with that address", it would hand back exactly
 * what the server just refused to say, and the whole exercise would be for
 * nothing. So the wording is deliberately about the ADDRESS rather than the
 * account: "if an account exists, we have sent a link".
 *
 * The only thing shown as a failure is a request that did not complete --
 * no network, or a 500. Those say nothing about whether the account exists.
 */
export default function ForgotPassword() {
  const { t, i18n } = useTranslation(['passwordReset', 'register']);
  const [email, setEmail] = useState('');
  // idle | sending | sent | failed | limited | unconfigured
  const [state, setState] = useState('idle');
  const [wait, setWait] = useState(1);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim() || state === 'sending') return;
    setState('sending');
    try {
      await api.post('/auth/forgot-password', { email: email.trim() });
      setState('sent');
    } catch (err) {
      // A 429 is about this browser's requests, not about the address.
      if (err.response?.status === 429) {
        setWait(waitMinutes(err.response.data));
        setState('limited');
      } else if (err.response?.data?.code === 'no_app_url') {
        // The server has no address to put in the link. It answers so for
        // every address, before looking any account up.
        setState('unconfigured');
      } else {
        setState('failed');
      }
    }
  };

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={styles.langRow}>
          <button type="button" style={styles.langBtn} title={t('tip.language')}
                  onClick={() => i18n.changeLanguage(i18n.language === 'en' ? 'he' : 'en')}>
            {i18n.language === 'en' ? 'עב' : 'EN'}
          </button>
        </div>
        <h1 style={styles.title}>{t('forgot.title')}</h1>

        {state === 'sent' ? (
          <div role="status" data-testid="forgot-sent">
            <p style={styles.success}>{t('forgot.sentTitle')}</p>
            <p style={styles.text}>{t('forgot.sentBody')}</p>
            <p style={styles.text}>{t('forgot.sentCheckSpam')}</p>
            <Link to="/login" title={t('tip.backToSignIn')} style={styles.link}>{t('forgot.backToSignIn')}</Link>
          </div>
        ) : (
          <form onSubmit={submit}>
            <p style={styles.text}>{t('forgot.help')}</p>
            <label style={styles.label} htmlFor="forgot-email">
              {t('register:email')}
            </label>
            <input
              id="forgot-email"
              type="email"
              autoComplete="email"
              dir="ltr"
              required
              value={email}
              onChange={(e) => { setEmail(e.target.value); if (state === 'failed') setState('idle'); }}
              style={styles.input}
              title={t('tip.forgotEmail')}
              data-testid="forgot-email"
            />
            {state === 'limited' && (
              <p role="alert" style={styles.problem} data-testid="forgot-limited">
                {t('register:errors.rate_limited', { minutes: wait })}
              </p>
            )}
            {state === 'failed' && (
              <p role="alert" style={styles.problem} data-testid="forgot-failed">
                {t('forgot.failed')}
              </p>
            )}
            {state === 'unconfigured' && (
              <p role="alert" style={styles.problem} data-testid="forgot-unconfigured">
                {t('register:errors.no_app_url')}
              </p>
            )}
            <button type="submit" disabled={state === 'sending' || !email.trim()}
                    style={{ ...styles.primary,
                             opacity: state === 'sending' || !email.trim() ? 0.5 : 1 }}
                    title={t('tip.forgotSubmit')}
                    data-testid="forgot-submit">
              {state === 'sending' ? t('forgot.sending') : t('forgot.submit')}
            </button>
          </form>
        )}

        {state !== 'sent' && (
          <p style={{ ...styles.text, marginTop: 16 }}>
            <Link to="/login" title={t('tip.backToSignIn')} style={styles.link}>{t('forgot.backToSignIn')}</Link>
          </p>
        )}
      </div>
    </div>
  );
}

/* Copied from VerifyEmail.js, which is the other page somebody reaches from
 * an email while signed out. Two pages in the same situation should not look
 * like two different products. */
const styles = {
  page: {
    minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: 'linear-gradient(160deg, var(--c-primary-soft-3) 0%, var(--c-primary-soft-4) 50%, var(--c-primary-soft-5) 100%)', padding: 16,
  },
  card: {
    width: '100%', maxWidth: 440, background: 'var(--c-surface)', borderRadius: 16, padding: '28px 32px',
    boxShadow: '0 8px 40px rgba(44, 110, 73, 0.12), 0 2px 8px rgba(0,0,0,0.06)',
  },
  langRow: { display: 'flex', justifyContent: 'flex-end' },
  langBtn: {
    border: '1px solid var(--c-border-strong)', borderRadius: 6, background: 'var(--c-surface)',
    padding: '4px 10px', cursor: 'pointer', fontSize: 12,
  },
  title: { fontSize: 20, color: 'var(--c-primary-fg)', margin: '8px 0 16px' },
  text: { fontSize: 14, color: 'var(--c-text-body-fg)', lineHeight: 1.5 },
  label: { display: 'block', fontSize: 13, fontWeight: 600, margin: '14px 0 4px' },
  input: {
    width: '100%', boxSizing: 'border-box', padding: '10px 12px', fontSize: 16,
    border: '1px solid var(--c-border-strong)', borderRadius: 8, background: 'var(--c-surface)',
  },
  primary: {
    width: '100%', marginTop: 16, padding: '11px 16px', border: 'none', borderRadius: 8,
    background: 'var(--c-primary)', color: 'var(--c-on-accent)', fontSize: 15, fontWeight: 600,
    cursor: 'pointer',
  },
  success: { fontSize: 15, fontWeight: 600, color: 'var(--c-success-3-fg)' },
  problem: { fontSize: 14, fontWeight: 600, color: 'var(--c-danger-2-fg)', marginTop: 10 },
  link: { color: 'var(--c-primary-fg)', fontWeight: 600 },
};
